"use strict";

const MAIN_COLLECTION_LIST_SELECTOR = ".js-main-collection__list";
const MAIN_COLLECTION_LOAD_MORE_BUTTON_SELECTOR = ".load-more-btn";
const MAIN_COLLECTION_LOAD_MORE_SPINNER_SELECTOR = ".load-more-spinner";
const MAIN_COLLECTION_LOAD_MORE_CONTAINER_SELECTOR = ".js-main-collection__load-more-container";
const MAIN_COLLECTION_ROOT_SELECTOR = "s-main-collection";
const MAIN_COLLECTION_PRODUCT_CARD_SELECTOR = "c-product-card-hover, c-product-card-slider";

function getMainCollectionPageSize(list) {
  const pageSize = Number.parseInt(list ? list.dataset.pageSize : "", 10);
  return Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 24;
}

function getMainCollectionVisibleProductCount(list) {
  return list ? list.querySelectorAll(MAIN_COLLECTION_PRODUCT_CARD_SELECTOR).length : 0;
}

function getMainCollectionProductKey(node) {
  if (!node || !node.matches || !node.matches(MAIN_COLLECTION_PRODUCT_CARD_SELECTOR)) {
    return "";
  }

  return node.dataset.handle || node.dataset.productId || node.getAttribute("data-handle") || "";
}

function ensureMainCollectionState(root) {
  if (!root) {
    return;
  }

  root._seenProductKeys = root._seenProductKeys instanceof Set ? root._seenProductKeys : new Set();
}

async function fetchNextCollectionPage(root, list, loadMoreContainer) {
  const nextUrl = list ? list.dataset.nextUrl : "";

  if (!root || !list || !loadMoreContainer || !nextUrl) {
    return false;
  }

  const response = await fetch(nextUrl, {
    headers: { "X-Requested-With": "XMLHttpRequest" }
  });

  if (!response.ok) {
    throw new Error(`Failed to load next page: ${response.status}`);
  }

  const nextPageHtml = await response.text();
  const parsedDocument = new DOMParser().parseFromString(nextPageHtml, "text/html");
  const nextRoot =
    parsedDocument.querySelector(`${MAIN_COLLECTION_ROOT_SELECTOR}[data-section-id="${root.dataset.sectionId}"]`) ||
    parsedDocument.querySelector(MAIN_COLLECTION_ROOT_SELECTOR);
  const nextList = nextRoot ? nextRoot.querySelector(MAIN_COLLECTION_LIST_SELECTOR) : null;
  const nextLoadMoreContainer = nextRoot
    ? nextRoot.querySelector(MAIN_COLLECTION_LOAD_MORE_CONTAINER_SELECTOR)
    : null;

  if (!nextList) {
    throw new Error("Missing collection list in next page");
  }

  const buffer = document.createElement("div");
  buffer.innerHTML = nextList.innerHTML;
  ensureMainCollectionState(root);

  Array.from(buffer.children).forEach((node) => {
    if (!node.matches || !node.matches(MAIN_COLLECTION_PRODUCT_CARD_SELECTOR)) {
      return;
    }

    const productKey = getMainCollectionProductKey(node);
    if (productKey && root._seenProductKeys.has(productKey)) {
      return;
    }

    if (productKey) {
      root._seenProductKeys.add(productKey);
    }

    list.appendChild(node);
  });

  list.dataset.nextUrl = nextList.dataset.nextUrl || "";
  loadMoreContainer.innerHTML = nextLoadMoreContainer ? nextLoadMoreContainer.innerHTML : "";

  return true;
}

async function fillMainCollectionVisibleSlots(root, targetVisibleCount) {
  const list = root ? root.querySelector(MAIN_COLLECTION_LIST_SELECTOR) : null;
  const loadMoreContainer = root ? root.querySelector(MAIN_COLLECTION_LOAD_MORE_CONTAINER_SELECTOR) : null;

  if (!root || !list || !loadMoreContainer) {
    return;
  }

  ensureMainCollectionState(root);

  while (getMainCollectionVisibleProductCount(list) < targetVisibleCount && list.dataset.nextUrl) {
    const didAppend = await fetchNextCollectionPage(root, list, loadMoreContainer);
    if (!didAppend) {
      break;
    }
  }
}

async function loadMoreCollectionProducts(button) {
  const root = button.closest(MAIN_COLLECTION_ROOT_SELECTOR);
  const list = root ? root.querySelector(MAIN_COLLECTION_LIST_SELECTOR) : null;
  const spinner = root ? root.querySelector(MAIN_COLLECTION_LOAD_MORE_SPINNER_SELECTOR) : null;
  const loadMoreContainer = root ? root.querySelector(MAIN_COLLECTION_LOAD_MORE_CONTAINER_SELECTOR) : null;

  if (!root || !list || !loadMoreContainer) {
    return true;
  }

  ensureMainCollectionState(root);

  const pageSize = getMainCollectionPageSize(list);
  const startCount = getMainCollectionVisibleProductCount(list);
  const targetVisibleCount = startCount + pageSize;

  button.hidden = true;
  button.disabled = true;

  if (spinner) {
    spinner.hidden = false;
  }

  try {
    await fillMainCollectionVisibleSlots(root, targetVisibleCount);
    return false;
  } catch (error) {
    console.error(error);
    if (list.dataset.nextUrl) {
      window.location.assign(list.dataset.nextUrl);
    }
    return false;
  } finally {
    if (spinner) {
      spinner.hidden = true;
    }
  }
}

if (!customElements.get("s-main-collection")) {
  customElements.define(
    "s-main-collection",
    class extends HTMLElement {
      connectedCallback() {
        this.sortDropdown = this.querySelector(".js-main-collection__sort-dropdown");
        this.sortButton = this.querySelector(".js-main-collection__sort-btn");
        this.sortInputs = Array.from(this.querySelectorAll(".js-main-collection__sort-input"));
        this.layoutButtons = Array.from(this.querySelectorAll(".js-main-collection__layout-button"));
        this.filterButton = this.querySelector(".js-main-collection__panel-filter");
        this.productsOverlay = this.querySelector(".js-main-collection__products-overlay");

        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleSortToggle = this.handleSortToggle.bind(this);
        this.handleSortChange = this.handleSortChange.bind(this);
        this.handleLayoutClick = this.handleLayoutClick.bind(this);
        this.handleOpenFilter = this.handleOpenFilter.bind(this);
        this.handleCloseAll = this.handleCloseAll.bind(this);

        if (this.sortButton) {
          this.sortButton.addEventListener("click", this.handleSortToggle);
          document.addEventListener("click", this.handleDocumentClick);
        }

        this.sortInputs.forEach((input) => input.addEventListener("change", this.handleSortChange));
        this.layoutButtons.forEach((button) => button.addEventListener("click", this.handleLayoutClick));

        if (this.filterButton) {
          this.filterButton.addEventListener("click", this.handleOpenFilter);
        }

        if (this.productsOverlay) {
          this.productsOverlay.addEventListener("click", this.handleCloseAll);
        }
      }

      disconnectedCallback() {
        if (this.sortButton) {
          this.sortButton.removeEventListener("click", this.handleSortToggle);
          document.removeEventListener("click", this.handleDocumentClick);
        }

        this.sortInputs.forEach((input) => input.removeEventListener("change", this.handleSortChange));
        this.layoutButtons.forEach((button) => button.removeEventListener("click", this.handleLayoutClick));

        if (this.filterButton) {
          this.filterButton.removeEventListener("click", this.handleOpenFilter);
        }

        if (this.productsOverlay) {
          this.productsOverlay.removeEventListener("click", this.handleCloseAll);
        }
      }

      handleDocumentClick(event) {
        if (this.contains(event.target)) {
          return;
        }

        this.closeSortDropdown();
      }

      handleSortToggle(event) {
        event.preventDefault();
        if (!this.sortDropdown) {
          return;
        }

        const isActive = this.sortDropdown.classList.contains("is-active");
        this.closeSortDropdown();

        if (!isActive) {
          this.sortDropdown.classList.add("is-active");
          if (this.productsOverlay) {
            this.productsOverlay.classList.add("is-active");
          }
        }
      }

      closeSortDropdown() {
        if (this.sortDropdown) {
          this.sortDropdown.classList.remove("is-active");
        }

        if (this.productsOverlay) {
          this.productsOverlay.classList.remove("is-active");
        }
      }

      handleCloseAll() {
        this.closeSortDropdown();
      }

      handleSortChange(event) {
        const input = event.currentTarget;
        const url = new URL(window.location.href);

        url.searchParams.set("sort_by", input.value);
        url.searchParams.delete("page");
        window.location.assign(url.toString());
      }

      handleLayoutClick(event) {
        const button = event.currentTarget;
        const layoutType = button.dataset.layout;
        const currentLayout = button.dataset.currentLayout;
        const nextLayout = button.dataset.changeLayout;
        const text = button.querySelector(".js-main-collection__layout-button-text");
        const icon = button.querySelector(".js-main-collection__layout-button-icon");

        if (!layoutType || !currentLayout || !nextLayout) {
          return;
        }

        this.classList.remove(`s-main-collection--${layoutType}-col-${currentLayout}`);
        this.classList.add(`s-main-collection--${layoutType}-col-${nextLayout}`);

        if (text) {
          text.textContent = button.dataset[`${nextLayout}LayoutText`] || text.textContent;
        }

        if (icon) {
          icon.classList.toggle("is-active--layout", nextLayout === "6" || nextLayout === "2");
        }

        button.dataset.currentLayout = nextLayout;
        button.dataset.changeLayout = currentLayout;
      }

      handleOpenFilter(event) {
        event.preventDefault();
        if (window.PubSub && typeof window.PubSub.publish === "function") {
          window.PubSub.publish("open-filter", event.currentTarget);
        }
      }
    }
  );
}

document.addEventListener("click", async (event) => {
  const button = event.target.closest(MAIN_COLLECTION_LOAD_MORE_BUTTON_SELECTOR);

  if (!button) {
    return;
  }

  event.preventDefault();
  await loadMoreCollectionProducts(button);
});

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(MAIN_COLLECTION_ROOT_SELECTOR).forEach((root) => {
    const list = root.querySelector(MAIN_COLLECTION_LIST_SELECTOR);
    if (!list) {
      return;
    }

    ensureMainCollectionState(root);
    Array.from(list.querySelectorAll(MAIN_COLLECTION_PRODUCT_CARD_SELECTOR)).forEach((node) => {
      const productKey = getMainCollectionProductKey(node);
      if (productKey) {
        root._seenProductKeys.add(productKey);
      }
    });

    fillMainCollectionVisibleSlots(root, getMainCollectionPageSize(list));
  });
});
